/**
 * Pure functions for governance artifact tree construction (SD-6).
 * Flat artifact list -> recursive tree; filename -> display label.
 */

import type { GovernanceArtifactItem } from "@/hooks/useGovernanceArtifacts";

export interface TreeNode {
  name: string;
  fullPath: string;
  children: TreeNode[];
  artifact?: GovernanceArtifactItem;
}

/**
 * Build a recursive tree from a flat list of governance artifacts.
 * Each artifact's `path` is split on `/` to produce folder and leaf nodes.
 * Folder nodes have children but no artifact; leaf nodes have an artifact.
 * Children are sorted alphabetically within each level.
 */
export function buildTree(artifacts: GovernanceArtifactItem[]): TreeNode[] {
  const root: TreeNode[] = [];

  for (const artifact of artifacts) {
    const segments = artifact.path.split("/");
    let current = root;
    let pathSoFar = "";

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      pathSoFar = pathSoFar ? `${pathSoFar}/${segment}` : segment;
      const isLeaf = i === segments.length - 1;

      let existing = current.find((n) => n.name === segment);
      if (!existing) {
        existing = {
          name: segment,
          fullPath: pathSoFar,
          children: [],
          ...(isLeaf ? { artifact } : {}),
        };
        current.push(existing);
      }

      if (isLeaf) {
        existing.artifact = artifact;
      }

      current = existing.children;
    }
  }

  sortTree(root);
  return root;
}

function sortTree(nodes: TreeNode[]): void {
  nodes.sort((a, b) => a.name.localeCompare(b.name));
  for (const node of nodes) {
    if (node.children.length > 0) {
      sortTree(node.children);
    }
  }
}

const LABEL_MAP: Record<string, string> = {
  "scope.md": "Scope",
  "design.md": "Design",
  "plan.md": "Plan",
  "story.md": "Story",
};

const ADR_PATTERN = /^adr-(\d+)/;

/**
 * Derive a human-readable display label from a governance artifact filename.
 * - scope.md -> "Scope", design.md -> "Design", plan.md -> "Plan"
 * - adr-133-tenant-isolation.md -> "ADR-133"
 * - anything else -> filename without extension
 */
export function displayLabel(filename: string): string {
  if (LABEL_MAP[filename]) {
    return LABEL_MAP[filename];
  }

  const adrMatch = ADR_PATTERN.exec(filename);
  if (adrMatch) {
    return `ADR-${adrMatch[1]}`;
  }

  // Strip .md extension
  return filename.replace(/\.md$/, "");
}
